import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, Col, Input, Progress, Row, Spin, Tag, Upload, message } from 'antd'
import {
    AppleOutlined,
    CheckCircleOutlined,
    CloudDownloadOutlined,
    CopyOutlined,
    DatabaseOutlined,
    HistoryOutlined,
    PictureOutlined,
    SafetyCertificateOutlined,
    SyncOutlined,
    ToolOutlined,
    WindowsOutlined,
} from '@ant-design/icons'
import { getCodexToolkitOverview, uploadCodexToolkitAsset } from '../../request'
import toolkitImagegenSettings from './assets/codex-toolkit-imagegen-settings.png'
import macosToolkitStillOpen from './assets/macos-toolkit-still-open.jpg'

const CAPABILITIES = [
    {
        icon: <HistoryOutlined />,
        title: '修复历史记录',
        description: '自动识别并修复模式切换后无法显示的历史记录，让重要工作上下文重新可见。',
    },
    {
        icon: <SyncOutlined />,
        title: '接续原来的会话',
        description: '在 OpenAI 账号与乐纯 API Key 模式之间切换后，仍可继续原来的 Codex 会话。',
    },
    {
        icon: <ToolOutlined />,
        title: '一键切换配置',
        description: '集中管理登录方式和 API 配置，减少手工修改配置文件带来的错误。',
    },
    {
        icon: <PictureOutlined />,
        title: '自动启用生图',
        description: '按需配置图像生成能力，不必再逐项查找和填写环境变量。',
    },
    {
        icon: <DatabaseOutlined />,
        title: '本地备份与恢复',
        description: '变更前自动保护本机配置和会话数据，遇到问题可以快速恢复。',
    },
    {
        icon: <SafetyCertificateOutlined />,
        title: '配置检查',
        description: '保存后自动检查关键配置是否生效，并给出清晰、可操作的处理提示。',
    },
]

const DOWNLOADS = [
    {
        key: 'macos-arm64',
        icon: <AppleOutlined />,
        title: 'macOS · Apple 芯片',
        description: '适用于 M1、M2、M3、M4 及更新型号的 Mac',
        file: 'DMG 安装包',
        accept: '.dmg',
        primary: true,
    },
    {
        key: 'macos-x64',
        icon: <AppleOutlined />,
        title: 'macOS · Intel 芯片',
        description: '适用于“关于本机”中显示 Intel 处理器的 Mac',
        file: 'DMG 安装包',
        accept: '.dmg',
    },
    {
        key: 'windows-x64',
        icon: <WindowsOutlined />,
        title: 'Windows · 64 位',
        description: '适用于公司常用的 Windows 10 / 11 电脑',
        file: 'EXE 安装程序',
        accept: '.exe,.msi',
    },
]

const STEPS = [
    ['01', '选择版本下载', '按照电脑系统和 Mac 芯片类型选择对应安装包。'],
    ['02', '安装并打开', '安装 CodexToolkit，按照界面提示完成首次配置。'],
    ['03', '保存并应用', '选择需要的模式并保存，CodexToolkit 会自动备份、修复和应用配置。'],
    ['04', '继续原会话', '完全退出并重新打开 Codex，即可查看历史记录并继续原来的工作。'],
]

const IMAGEGEN_STEPS = [
    ['01', '在 CodexToolkit 启用', '选择乐纯 API Key 模式，确认“配置生图能力”已勾选，保存后完全退出并重新打开 Codex。'],
    ['02', '明确调用方式', '告诉 Codex 使用 $imagegen 技能和 API/CLI 备用模式，不使用内置 image_gen 工具。'],
    ['03', '描述并保存图片', '写清画面内容、比例、风格和限制，并指定图片保存到当前项目中的路径。'],
]

const IMAGEGEN_PROMPT = `请使用 $imagegen 技能，并明确采用 API/CLI 备用模式生成图片，不要使用内置 image_gen 工具。

使用 gpt-image-2 模型，生成一张[图片内容]。要求：[横版/竖版和尺寸]，[视觉风格与色调]，[人物、文字、Logo、水印等限制]。

将图片保存到当前项目的 output/imagegen/[文件名].png。如果检测到生图环境变量未配置，请提示我打开 CodexToolkit 启用“生图能力”并保存，不要让我在聊天中发送 API Key。`

// 2026-08-17 coder(lq): 从安装包文件名提取三段式版本号，识别失败时仍允许管理员手工填写。
const extractVersionFromFileName = fileName => {
    const match = String(fileName || '').match(/(?:^|[^0-9.])v?(\d+\.\d+\.\d+)(?=[^0-9.]|$)/i)
    return match ? match[1] : ''
}

function CodexToolkitTab() {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [assets, setAssets] = useState([])
    const [canManage, setCanManage] = useState(false)
    const [versions, setVersions] = useState({})
    const [uploadProgress, setUploadProgress] = useState({})

    const loadOverview = () => {
        setLoading(true)
        setError('')
        return getCodexToolkitOverview().then(result => {
            const overview = result || {}
            const nextAssets = overview.assets || []
            setAssets(nextAssets)
            setCanManage(Boolean(overview.canManage))
            setVersions(current => {
                const next = { ...current }
                DOWNLOADS.forEach(download => {
                    const asset = nextAssets.find(item => item.platform === download.key)
                    if (!next[download.key]) next[download.key] = asset?.version || '0.1.0'
                })
                return next
            })
        }).catch(requestError => {
            setError(requestError?.message || '安装包信息加载失败')
        }).finally(() => setLoading(false))
    }

    useEffect(() => {
        loadOverview()
    }, [])

    const scrollToDownloads = () => {
        const target = document.getElementById('codex-toolkit-downloads')
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const uploadAsset = (platform, options) => {
        const detectedVersion = extractVersionFromFileName(options.file?.name)
        const version = detectedVersion || (versions[platform] || '').trim()
        if (detectedVersion) {
            setVersions(current => ({ ...current, [platform]: detectedVersion }))
        }
        if (!/^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/.test(version)) {
            message.warning('文件名中未识别到版本号，请先填写正确版本号，例如 1.0.1')
            options.onError(new Error('版本号格式不正确'))
            return
        }
        setUploadProgress(current => ({ ...current, [platform]: 1 }))
        uploadCodexToolkitAsset(platform, version, options.file, event => {
            const percent = event.total ? Math.round(event.loaded * 100 / event.total) : 0
            setUploadProgress(current => ({ ...current, [platform]: percent }))
            options.onProgress({ percent })
        }).then(() => {
            message.success('安装包已上传到 OSS 并立即生效')
            options.onSuccess()
            return loadOverview()
        }).catch(uploadError => {
            setError(uploadError?.message || '安装包上传失败')
            options.onError(uploadError)
        }).finally(() => setUploadProgress(current => {
            const next = { ...current }
            delete next[platform]
            return next
        }))
    }

    const fillVersionFromFileName = (platform, file) => {
        const detectedVersion = extractVersionFromFileName(file?.name)
        if (detectedVersion) {
            setVersions(current => ({ ...current, [platform]: detectedVersion }))
        }
        return true
    }

    const copyImagegenPrompt = () => {
        if (!navigator.clipboard) {
            message.warning('当前浏览器不支持一键复制，请手动选择模板文字复制')
            return
        }
        navigator.clipboard.writeText(IMAGEGEN_PROMPT)
            .then(() => message.success('生图指令模板已复制'))
            .catch(() => message.warning('复制失败，请手动选择模板文字复制'))
    }

    return (
        <div className="profile-codex-toolkit-wrap">
            <section className="profile-codex-toolkit-hero">
                <div className="profile-codex-toolkit-hero-copy">
                    <Tag color="blue">乐纯官方桌面工具</Tag>
                    <h1>CodexToolkit</h1>
                    <h2 className="profile-codex-toolkit-tagline">安全切换配置，历史会话不断档</h2>
                    <p>
                        CodexToolkit 帮你安全切换 OpenAI 账号与乐纯 API Key 模式，
                        自动保护并修复历史记录，让切换前的会话仍能继续使用。
                    </p>
                    <div className="profile-codex-toolkit-actions">
                        <Button type="primary" size="large" icon={<CloudDownloadOutlined />} onClick={scrollToDownloads}>
                            选择版本下载
                        </Button>
                        <span><CheckCircleOutlined /> 配置保存在本机，不会在页面展示 API Key</span>
                    </div>
                </div>
                <div className="profile-codex-toolkit-hero-mark" aria-hidden="true">
                    <ToolOutlined />
                </div>
            </section>

            <section className="profile-codex-toolkit-section">
                <div className="profile-codex-toolkit-heading">
                    <Tag color="geekblue">核心优势</Tag>
                    <h2>替你处理切换背后的复杂细节</h2>
                    <p>不用手工寻找配置文件，也不必担心切换后找不到之前的工作。</p>
                </div>
                <Row gutter={[18, 18]}>
                    {CAPABILITIES.map(item => (
                        <Col xs={24} md={12} xl={8} key={item.title}>
                            <Card bordered={false} className="profile-codex-toolkit-capability">
                                <div className="profile-codex-toolkit-capability-icon">{item.icon}</div>
                                <div>
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </section>

            <section className="profile-codex-toolkit-section profile-codex-toolkit-flow">
                <div className="profile-codex-toolkit-heading">
                    <Tag color="cyan">使用流程</Tag>
                    <h2>四步完成配置</h2>
                </div>
                <div className="profile-codex-toolkit-step-grid">
                    {STEPS.map(([number, title, description]) => (
                        <div className="profile-codex-toolkit-step" key={number}>
                            <span>{number}</span>
                            <h3>{title}</h3>
                            <p>{description}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="profile-codex-toolkit-section profile-codex-toolkit-imagegen">
                <div className="profile-codex-toolkit-heading">
                    <Tag color="purple">AI 生图</Tag>
                    <h2>在 Codex 里一句话生成图片</h2>
                    <p>用 CodexToolkit 完成一次配置后，直接在任务中告诉 Codex 生成什么图片即可。</p>
                </div>
                <figure className="profile-codex-toolkit-imagegen-guide">
                    <img src={toolkitImagegenSettings} alt="CodexToolkit 设置页面中的同时配置 Codex 生图能力选项和保存切换设置按钮" />
                    <figcaption>
                        打开 CodexToolkit 的“设置”，勾选“同时配置 Codex 生图能力”，点击“保存切换设置”，然后完全退出并重新打开 Codex。
                    </figcaption>
                </figure>
                <div className="profile-codex-toolkit-imagegen-grid">
                    {IMAGEGEN_STEPS.map(([number, title, description]) => (
                        <div className="profile-codex-toolkit-imagegen-step" key={number}>
                            <span>{number}</span>
                            <h3>{title}</h3>
                            <p>{description}</p>
                        </div>
                    ))}
                </div>
                <div className="profile-codex-toolkit-prompt">
                    <div className="profile-codex-toolkit-prompt-heading">
                        <div>
                            <PictureOutlined />
                            <strong>可直接复制给 Codex 的生图指令</strong>
                        </div>
                        <Button icon={<CopyOutlined />} onClick={copyImagegenPrompt}>复制指令</Button>
                    </div>
                    <pre>{IMAGEGEN_PROMPT}</pre>
                    <p><SafetyCertificateOutlined /> API Key 只在本机通过 CodexToolkit 配置，请勿在 Codex 对话中发送密钥。</p>
                </div>
            </section>

            <section className="profile-codex-toolkit-section" id="codex-toolkit-downloads">
                <div className="profile-codex-toolkit-heading">
                    <Tag color="green">下载安装</Tag>
                    <h2>下载 CodexToolkit</h2>
                    <p>请选择适合你电脑的版本。Mac 用户可点击左上角苹果菜单 → 关于本机，查看芯片或处理器类型。</p>
                </div>
                {error && <Alert showIcon closable type="error" message={error} onClose={() => setError('')} className="profile-codex-toolkit-error" />}
                <Spin spinning={loading}>
                <Row gutter={[18, 18]}>
                    {DOWNLOADS.map(item => {
                        const asset = assets.find(candidate => candidate.platform === item.key)
                        const progress = uploadProgress[item.key]
                        return (
                        <Col xs={24} lg={8} key={item.title}>
                            <Card bordered={false} className={`profile-codex-toolkit-download${item.primary ? ' is-primary' : ''}`}>
                                <div className="profile-codex-toolkit-download-icon">{item.icon}</div>
                                <h3>{item.title}</h3>
                                <p>{item.description}</p>
                                <div className="profile-codex-toolkit-version">
                                    <Tag>{item.file}</Tag>
                                    {asset && <Tag color="blue">v{asset.version}</Tag>}
                                </div>
                                <Button type={item.primary ? 'primary' : 'default'} block size="large" icon={<CloudDownloadOutlined />}
                                    href={asset?.url} disabled={!asset?.url}>
                                    {asset?.url ? '立即下载' : '暂未上传'}
                                </Button>
                                {canManage && <div className="profile-codex-toolkit-admin-upload">
                                    <Input size="small" addonBefore="版本" value={versions[item.key] || ''}
                                        onChange={event => setVersions(current => ({ ...current, [item.key]: event.target.value }))} />
                                    <Upload accept={item.accept} showUploadList={false}
                                        beforeUpload={file => fillVersionFromFileName(item.key, file)}
                                        customRequest={options => uploadAsset(item.key, options)}>
                                        <Button block icon={<CloudDownloadOutlined />} loading={progress !== undefined}>
                                            {asset ? '替换 OSS 安装包' : '上传到 OSS'}
                                        </Button>
                                    </Upload>
                                    {progress !== undefined && <Progress percent={progress} size="small" />}
                                </div>}
                            </Card>
                        </Col>
                    )})}
                </Row>
                </Spin>
                {canManage && <Alert showIcon type="info" className="profile-codex-toolkit-admin-tip"
                    message="管理员上传模式"
                    description="你拥有 A2A 管理权限，可以直接上传各平台的新安装包。上传成功后立即切换当前下载版本，OSS 中的历史安装包会继续保留。" />}
                <div className="profile-codex-toolkit-note">
                    <SafetyCertificateOutlined />
                    <div>
                        <strong>安装提示</strong>
                        <p>
                            <b>macOS 提示“应用已损坏”或“无法打开”怎么办？</b>
                            请先确认安装包来自公司 CodexToolkit 页面。若 macOS 阻止打开 CodexToolkit，请打开
                            <b>系统设置 → 隐私与安全性</b>，向下滚动到“安全性”部分，在“已阻止 CodexToolkit 以保护 Mac”旁点击
                            <b>仍要打开</b>，然后按系统提示确认。
                        </p>
                        <img className="profile-codex-toolkit-gatekeeper-guide" src={macosToolkitStillOpen}
                            alt="macOS 隐私与安全性页面中的仍要打开按钮" />
                        <p>
                            如果没有看到“仍要打开”，请先尝试启动一次 CodexToolkit，再立即回到“隐私与安全性”页面查看。
                            如果系统设置方式仍无法解决，可打开终端执行：
                            <code>xattr -dr com.apple.quarantine &quot;/Applications/CodexToolkit.app&quot;</code>
                            安装和切换前请先完全退出 Codex。
                        </p>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default CodexToolkitTab
